import type { CreateCircularInput } from '@qualicloud/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { PeoplePicker } from '@/components/sgq/people-picker';
import {
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Field,
  FormError,
  IconAlertTriangle,
  IconInfo,
  Input,
  NumberInput,
  Switch,
  Textarea,
  useToast,
} from '@/design-system';
import { circularsApi, queryKeys, toUserMessage } from '@/lib/api';
import { useDirectory } from '@/lib/use-directory';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NOVA CIRCULAR — sgq.md §D.1
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A circular NASCE EM RASCUNHO. É a diferença estrutural para o legado, onde
 * ela nascia publicada, o e-mail saía de dentro do `add` e não havia endpoint
 * de edição: um erro de digitação no título virava evidência permanente
 * (BUG-CIR-07). Separar criação de publicação permite revisar antes de o texto
 * ficar imutável.
 *
 * Os limites de tamanho são os do `validateForm` do legado, preservados no
 * schema: título de 10 a 255 caracteres, descrição de 10 a 3000.
 *
 * DESTINATÁRIOS: usuários nominais, departamentos inteiros ou toda a
 * organização. É a mesma semântica dos prefixos `cl_<DEP>_<USER>` e
 * `dp_<DEP>_<USER>` do legado, sem depender de nomes de campo codificados.
 */
export function CircularFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const directory = useDirectory();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [recipientUserIds, setRecipientUserIds] = useState<string[]>([]);
  const [recipientDepartmentIds, setRecipientDepartmentIds] = useState<string[]>([]);
  const [allOrganization, setAllOrganization] = useState(false);
  const [dueDays, setDueDays] = useState('15');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: (): Promise<{ id: string; code: string }> => {
      const input: CreateCircularInput = {
        title,
        body,
        recipientUserIds,
        recipientDepartmentIds,
        recipientAllOrganization: allOrganization,
        actionIds: [],
        items: [],
        acknowledgementDueDays: Number(dueDays) || 15,
      };
      return circularsApi.create(input);
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.circulars.all() });
      toast({
        tone: 'success',
        title: `Circular ${created.code} criada como rascunho`,
        description:
          'Revise o texto e os destinatários antes de publicar. Depois da publicação o conteúdo é imutável — a correção passa a ser uma nova circular.',
      });
      void navigate({ to: '/circulares/$circularId', params: { circularId: created.id } });
    },
    onError: (error) => setSubmitError(toUserMessage(error)),
  });

  const titleInvalid = title.trim().length > 0 && (title.trim().length < 10 || title.length > 255);
  const bodyInvalid = body.trim().length > 0 && (body.trim().length < 10 || body.length > 3000);
  const noRecipients =
    !allOrganization && recipientUserIds.length === 0 && recipientDepartmentIds.length === 0;

  const canSubmit =
    title.trim().length >= 10 &&
    title.length <= 255 &&
    body.trim().length >= 10 &&
    body.length <= 3000 &&
    !noRecipients;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Circulares', to: '/circulares' }, { label: 'Nova' }]}
        title="Criar circular"
        description="A circular é criada como rascunho. A publicação é um passo à parte: é a partir dela que o texto fica imutável e a ciência começa a ser cobrada."
      />

      <PageBody>
        <form
          noValidate
          className="flex max-w-4xl flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitError(null);
            submit.mutate();
          }}
        >
          {submitError ? (
            <FormError title="Não foi possível criar a circular">{submitError}</FormError>
          ) : null}

          <Card className="flex flex-col gap-4">
            <CardHeader headingLevel={2} title="Dados da circular" />

            <Field
              label="Título"
              required
              error={titleInvalid ? 'O título deve ter entre 10 e 255 caracteres.' : undefined}
            >
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={255}
                autoFocus
              />
            </Field>

            <Field
              label="Comunicado"
              required
              error={bodyInvalid ? 'O comunicado deve ter entre 10 e 3000 caracteres.' : undefined}
              description="Depois de publicada, esta é a versão que os destinatários assinam ter lido."
            >
              <Textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={10}
                maxLength={3000}
                showCounter
              />
            </Field>
          </Card>

          <Card className="flex flex-col gap-4">
            <CardHeader
              headingLevel={2}
              title="Destinatários"
              description="Quem precisa tomar ciência. Departamentos são expandidos em pessoas pelo servidor no momento da publicação."
            />

            <Switch
              checked={allOrganization}
              onCheckedChange={setAllOrganization}
              label="Enviar para toda a organização"
              stateLabels={{ on: 'Toda a organização', off: 'Destinatários escolhidos' }}
              description="Com esta opção ligada, a circular vai para todos os colaboradores; os destinatários escolhidos abaixo são acrescentados a eles."
            />

            <PeoplePicker
              noun="destinatários"
              userFieldLabel="Adicionar colaborador"
              userFieldDescription="Cada colaborador recebe uma pendência de ciência individual, com prazo próprio."
              departmentFieldLabel="Adicionar departamento"
              departmentFieldDescription="Todos os membros do departamento, resolvidos pelo servidor na publicação."
              users={directory.subjects.map((subject) => ({
                id: subject.userId,
                name: subject.displayName,
                hint: subject.jobTitle,
              }))}
              departments={directory.departments.map((department) => ({
                id: department.id,
                name: department.name,
              }))}
              userIds={recipientUserIds}
              onUserIdsChange={setRecipientUserIds}
              departmentIds={recipientDepartmentIds}
              onDepartmentIdsChange={setRecipientDepartmentIds}
              error={noRecipients ? 'Informe ao menos um destinatário.' : undefined}
              emptyHint="Sem destinatário a circular não produz evidência de comunicação: ninguém é chamado a dar ciência."
            />

            {noRecipients ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
              >
                <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  <strong className="font-semibold">A circular não alcançará ninguém.</strong>{' '}
                  Escolha destinatários ou ligue o envio para toda a organização.
                </span>
              </p>
            ) : null}

            <Field
              label="Prazo para a ciência (dias)"
              description="Conta a partir da publicação. Alimenta o lembrete automático de circular sem ciência."
            >
              <NumberInput
                value={dueDays}
                onChange={(event) => setDueDays(event.target.value)}
                min={1}
                max={365}
                className="w-28"
              />
            </Field>
          </Card>

          <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
            <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Criar não publica. A circular fica em rascunho até você acionar “Publicar” na tela do
              registro — e só então os destinatários são notificados e o texto se torna imutável.
            </span>
          </p>

          <ButtonGroup>
            <Button
              variant="secondary"
              disabled={submit.isPending}
              onClick={() => void navigate({ to: '/circulares' })}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submit.isPending}
              disabled={!canSubmit}
            >
              Criar circular
            </Button>
          </ButtonGroup>
        </form>
      </PageBody>
    </>
  );
}
